import {
  applyOps,
  applyToPoint,
  findSymmetry,
  growRun,
  invert,
  shrinkFromFurthest,
  shrinkRun,
  nearestSegment,
  pointOnSegment,
  segmentsOf,
  smoothHandles,
  snapDragPoint,
  withPointAdded,
  type Matrix,
  type PathPoint,
  type SnapGuide,
  type SnapLine,
  type SnapSettings,
  type Scene,
  type Subpath,
  type VectorPath,
} from '@flobi/artwork-core';
import type * as Y from 'yjs';
import type { Point } from './penTool';
import { anchorCount, simplifyVectorPath } from './simplifyPath';

/** What the pointer is over, or holding. */
export interface PathGrab {
  readonly kind: 'anchor' | 'in' | 'out';
  readonly index: number;
}

/**
 * A piece of path under the pointer: which one, and where along it.
 *
 * `at` is where on the piece the pointer actually is, which is what decides whether it is
 * near enough to act on. `middle` is the halfway mark, where the point on offer is drawn
 * and where pressing it puts an anchor.
 */
export interface SegmentHover {
  readonly index: number;
  readonly t: number;
  readonly at: Point;
  readonly middle: Point;
}

/**
 * Where one subpath's anchors sit in the open path's single run of indices.
 *
 * Every subpath is editable, so an index names an anchor across all of them: the first
 * subpath's anchors come first, then the second's, and so on. A segment is named by the
 * index of the anchor it leaves, so an open subpath's last anchor starts no segment.
 */
export interface SubpathRun {
  readonly start: number;
  readonly count: number;
  readonly closed: boolean;
}

/** What the overlay draws for a path being edited. */
export interface PathEditPreview {
  readonly id: string;
  /** Every subpath's anchors, one after another (see `SubpathRun`). */
  readonly points: readonly PathPoint[];
  /** Whether the focused subpath is closed. */
  readonly closed: boolean;
  readonly runs: readonly SubpathRun[];
  /** The subpath the chosen anchors are on (else the first): what Close path acts on. */
  readonly focus: SubpathRun;
  /** Anchors the person has selected; their handles are the ones shown. */
  readonly selected: readonly number[];
  readonly hovered: PathGrab | null;
  /** The piece of path under the pointer, if it is near enough to act on. */
  readonly segment: SegmentHover | null;
}

/** How near an anchor or handle the pointer counts as being, in screen pixels. */
/** A press this close to a point takes the point, whatever else is near. */
const POINT_FIRST = 3;
const REACH = 8;
/** How near a piece of path counts as being over it, in screen pixels. */
const SEGMENT_REACH = 6;

/**
 * Says when the points have closed and stayed closed, whatever closed them: a click on
 * nothing, a layer picked in the list, the path deleted or undone away.
 *
 * Called on every change to the points, it tells once the hand that made the change has
 * finished rather than at once, because choosing another path closes this one only to open
 * that one, and that is not leaving. Without it only Escape and Done went back to choosing
 * whole shapes; a click off the shape left the Edit Points strip up with nothing to edit.
 */
export function whenPointsLeft(open: () => boolean, tell: () => void): () => void {
  let waiting = false;
  return () => {
    if (open() || waiting) return;
    waiting = true;
    queueMicrotask(() => {
      waiting = false;
      if (!open()) tell();
    });
  };
}

/**
 * Editing a path after it is drawn: moving anchors, pulling handles, removing points.
 *
 * A pen that can only draw is a toy — the shape is made in the second pass, and this is
 * that pass. Every subpath is editable: the hole in an "e" or an "o" is points like any
 * others. Their anchors are numbered as one run (see `SubpathRun`), so choosing, moving
 * and deleting work across subpaths, while anything about neighbours — segments, adding
 * a point, smoothing, growing a selection — stays inside the subpath an anchor is on.
 */
export class PathEditing {
  private id: string | null = null;
  private selected: number[] = [];
  /**
   * How the selection grew, so shrinking can walk back exactly the way it came.
   *
   * Each entry is what was chosen before a grow and what it grew to. Shrinking pops the
   * top only if the selection is still what that grow produced — anything else touching
   * the selection leaves the stack behind rather than letting it give a wrong answer.
   */
  private grown: { from: number[]; to: number[] }[] = [];
  /** Whether choosing an anchor also chooses the one it mirrors onto. */
  private symmetric = false;
  /** The axis found for the open path, worked out once per shape rather than per press. */
  private mirror: { id: string; shape: string; pairs: readonly number[] | null } | null = null;
  private grabbed: PathGrab | null = null;
  private hovered: PathGrab | null = null;
  private segment: SegmentHover | null = null;
  /** Where the drag started, and the point as it was, so a drag is one movement. */
  private from: Point | null = null;
  private before: PathPoint | null = null;
  /** The whole path as the drag found it, for when the drag is carrying several anchors. */
  private beforeAll: PathPoint[] | null = null;
  /** The lines the last move agreed with, drawn until the drag ends. */
  private agreed: readonly SnapLine[] = [];

  constructor(
    private readonly options: {
      page: Y.Doc;
      scene: Scene;
      origin: unknown;
      toScreen: (point: Point) => Point;
      /** How near counts as level, in document units at the current zoom. */
      snapReach: () => number;
      /** Everything on screen a dragged anchor may line itself up with, in page coordinates. */
      snapTargets: () => Point[];
      /**
       * What a drag is allowed to be pulled by, as the view's four toggles say.
       *
       * The canvas has already dropped from `snapTargets` whatever the toggles exclude,
       * so anything still in that list is by definition allowed to pull — which is why
       * the canvas hands down a settings object whose `smart` is on whenever EITHER
       * smart guides or snap-to-point is. Reading the flag here as well would gate the
       * same decision twice, and an anchor could then never land on another anchor with
       * smart guides off, which is the one thing snap-to-point is for.
       */
      snapSettings: () => SnapSettings;
      /** The guides a dragged anchor may land on, in page coordinates. */
      snapGuides: () => SnapGuide[];
      onChange: () => void;
    },
  ) {}

  get editingId(): string | null {
    return this.id;
  }

  /** Why the last move stopped where it did, in page coordinates, for the overlay. */
  get guides(): readonly SnapLine[] {
    return this.agreed;
  }

  /**
   * Everything a dragged anchor may line itself up with, in page coordinates.
   *
   * Its own path's anchors that are not travelling, and then everything on screen — the
   * canvas gathers that half, because what is in view is the camera's business and not a
   * path's. On screen is the right bound: a point that agreed with something nobody can
   * see would look like a point that stopped for no reason.
   */
  private settled(): Point[] {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!this.id || !points) return this.options.snapTargets();
    const moving = new Set(this.selected);
    const world = this.options.scene.worldTransform(this.id);
    const mine = points.flatMap((point, index) => (moving.has(index) ? [] : [applyToPoint(world, point.x, point.y)]));
    return [...mine, ...this.options.snapTargets()];
  }

  get active(): boolean {
    return this.id !== null;
  }

  open(id: string): boolean {
    if (!this.pointsOf(id)) return false;
    // Not a path nobody can see or one inside a locked group: its points could be caught
    // and moved though nothing else could reach it (QA 2026-09-25, select-points-F19).
    if (!this.options.scene.choosable(id)) return false;
    this.id = id;
    this.selected = [];
    // The axis is found afresh for what is opened, never carried over (select-points-F21).
    this.mirror = null;
    this.options.onChange();
    return true;
  }

  close(): void {
    if (!this.id) return;
    this.id = null;
    this.selected = [];
    this.grabbed = null;
    this.hovered = null;
    this.segment = null;
    this.options.onChange();
  }

  /**
   * Where a path's anchors are on the page, for any path, open or not.
   *
   * A region has to be asked about every candidate before one of them is opened, so this
   * deliberately does not read `this.id`: the marquee needs to know which path caught the
   * most points before it can decide which path to open.
   */
  anchorsOf(id: string): { index: number; at: Point }[] | null {
    const points = this.pointsOf(id);
    if (!points) return null;
    const world = this.options.scene.worldTransform(id);
    return points.map((point, index) => ({ index, at: applyToPoint(world, point.x, point.y) }));
  }

  /**
   * The anchor of any path near a point, so one can be taken hold of directly.
   *
   * Nothing here reads `this.id`: the whole purpose is to reach an anchor on a shape that
   * is not open yet, so that coming near a point is enough to choose it and nobody has to
   * select the shape first and the point second.
   */
  anchorNear(world: Point, ids: Iterable<string>): { id: string; index: number } | null {
    const pointer = this.options.toScreen(world);
    let best: { id: string; index: number; away: number } | null = null;
    for (const id of ids) {
      for (const anchor of this.anchorsOf(id) ?? []) {
        const at = this.options.toScreen(anchor.at);
        const away = Math.hypot(at.x - pointer.x, at.y - pointer.y);
        if (away <= REACH && (!best || away < best.away)) best = { id, index: anchor.index, away };
      }
    }
    return best ? { id: best.id, index: best.index } : null;
  }

  /** A path's outline on the page, one line per subpath, for tracing the shape under the pointer. */
  outlineOf(id: string): Point[][] | null {
    const subpaths = this.subpathsOf(id);
    if (!subpaths) return null;
    const world = this.options.scene.worldTransform(id);
    const lines = subpaths.flatMap(({ points, closed }) => {
      if (points.length < 2) return [];
      const out: Point[] = [];
      for (const segment of segmentsOf(points, closed)) {
        for (let step = 0; step < 12; step++) {
          const at = pointOnSegment(segment, step / 12);
          out.push(applyToPoint(world, at.x, at.y));
        }
      }
      const last = points[closed ? 0 : points.length - 1]!;
      out.push(applyToPoint(world, last.x, last.y));
      return [out];
    });
    return lines.length ? lines : null;
  }

  /** Where the chosen anchors are on the page, which is what a frame is drawn round. */
  chosenAnchors(): Point[] {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!this.id || !points) return [];
    const world = this.options.scene.worldTransform(this.id);
    return this.selected
      .map((index) => points[index])
      .filter((point): point is PathPoint => !!point)
      .map((point) => applyToPoint(world, point.x, point.y));
  }

  /**
   * The open path's points as they are now, for a gesture to transform from.
   *
   * A drag has to work from where the points were when it started, not from where it
   * put them a frame ago — otherwise every frame compounds the last and a slow drag
   * moves further than a fast one over the same distance.
   */
  snapshot(): PathPoint[] | null {
    const points = this.id ? this.pointsOf(this.id) : null;
    return points ? points.map((point) => ({ ...point })) : null;
  }

  /**
   * Writes the chosen anchors as `from` put through a matrix given in page coordinates.
   *
   * The matrix is in page coordinates because that is where the frame the person is
   * dragging lives; each point makes the round trip out to the page and back, so a path
   * that is itself turned or scaled is moved by what the eye sees rather than by its own
   * skewed axes. Handles travel with their anchor, which is what keeps a curve a curve.
   */
  transformChosen(from: readonly PathPoint[], world: Matrix): void {
    const id = this.id;
    if (!id || this.selected.length === 0) return;
    const out = this.options.scene.worldTransform(id);
    const back = invert(out);
    if (!back) return;
    const move = (x: number, y: number): [number, number] => {
      const onPage = applyToPoint(out, x, y);
      const moved = applyToPoint(world, onPage.x, onPage.y);
      const local = applyToPoint(back, moved.x, moved.y);
      return [local.x, local.y];
    };
    const chosen = new Set(this.selected);
    this.write(
      from.map((point, index) => {
        if (!chosen.has(index)) return { ...point };
        const [x, y] = move(point.x, point.y);
        return {
          ...point,
          x,
          y,
          ...(point.in ? { in: move(point.in[0], point.in[1]) } : {}),
          ...(point.out ? { out: move(point.out[0], point.out[1]) } : {}),
        };
      }),
    );
  }

  /**
   * Whether the open path is symmetrical enough to pair its anchors across an axis.
   *
   * Worked out once per shape and remembered, because the answer costs a pass over every
   * anchor against every other and nothing about it changes between two presses. A path
   * with thousands of anchors is left alone: the answer would cost more than it is worth
   * and nobody edits a traced outline by symmetry.
   */
  get canMirror(): boolean {
    return this.pairing() !== null;
  }

  get mirroring(): boolean {
    return this.symmetric && this.canMirror;
  }

  setMirroring(on: boolean): void {
    this.symmetric = on;
    // Turning it on takes in what is already chosen, so the rail's press is the whole act.
    if (on) this.selectIndices(this.selected, false);
    else this.options.onChange();
  }

  /** The chosen anchors plus the ones either side: adjacency on a path is exact. */
  grow(): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points || this.selected.length === 0) return;
    const from = [...this.selected];
    this.selectIndices(this.perRun(this.selected, growRun), false);
    if (this.selected.length !== from.length) this.grown.push({ from, to: [...this.selected] });
  }

  /**
   * Fewer anchors, in the reverse of however they came to be chosen.
   *
   * Three rules in order, and the order is the whole design. If a grow put us here, go
   * back to exactly where it grew from — a dial should walk back the way it walked
   * forward. Otherwise take the ends off each run, which is what growing does undone.
   * And if that removes nothing — a closed path wholly chosen has no ends at all, so the
   * topology has no answer — drop whatever sticks out furthest from the middle, so the
   * button is never simply dead in the hand.
   */
  shrink(): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points || this.selected.length === 0) return;

    const top = this.grown[this.grown.length - 1];
    if (top && same(top.to, this.selected)) {
      this.grown.pop();
      // Straight to the field: the mirror must not put back what this just took off.
      this.selected = top.from.filter((index) => index < points.length);
      this.options.onChange();
      return;
    }

    const trimmed = this.perRun(this.selected, shrinkRun);
    this.selected =
      trimmed.length < this.selected.length
        ? trimmed
        : shrinkFromFurthest(
            this.selected,
            points.map((point) => ({ x: point.x, y: point.y, width: 0, height: 0 })),
          );
    this.options.onChange();
  }

  /** Every anchor on the path: what growing a few dozen times would arrive at. */
  takeAll(): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points) return;
    this.grown = [];
    this.selected = points.map((_, index) => index);
    this.options.onChange();
  }

  /** Everything on the path that is not chosen, which is the cheapest useful reach. */
  invert(): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points) return;
    const was = new Set(this.selected);
    this.grown = [];
    this.selected = points.map((_, index) => index).filter((index) => !was.has(index));
    this.options.onChange();
  }

  /** The axis for the open path, found once and kept until the path changes shape. */
  private pairing(): readonly number[] | null {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!this.id || !points || points.length < 2) return null;
    if (points.length > 400) return null;
    // Kept for the shape as it IS, not for its count: dragging points out of symmetry kept
    // the count, so the old axis went on pairing points that no longer mirrored
    // (QA 2026-09-25, select-points-F21). A few hundred numbers is cheap to compare.
    const shape = points.map((point) => `${point.x},${point.y}`).join(';');
    if (this.mirror && this.mirror.id === this.id && this.mirror.shape === shape) return this.mirror.pairs;
    const found = findSymmetry(points.map((point) => ({ x: point.x, y: point.y })));
    this.mirror = { id: this.id, shape, pairs: found?.pairs ?? null };
    return this.mirror.pairs;
  }

  /** Chooses anchors by index, as a region does when it lets go. */
  selectIndices(indices: readonly number[], extend: boolean): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points) return;
    const pairs = this.symmetric ? this.pairing() : null;
    const valid = (pairs ? [...new Set(indices.flatMap((index) => [index, pairs[index] ?? index]))] : indices)
      .filter((index) => index >= 0 && index < points.length);
    // Extending adds rather than toggles: dragging a second loop over a point already
    // chosen should keep it, not quietly drop it.
    this.selected = extend ? [...new Set([...this.selected, ...valid])] : valid;
    this.grown = [];
    this.options.onChange();
  }

  preview(): PathEditPreview | null {
    const points = this.id ? this.pointsOf(this.id) : null;
    const runs = this.id ? this.runsOf(this.id) : null;
    if (!this.id || !points || !runs) return null;
    const focus = this.focusRun(runs);
    return {
      id: this.id,
      points,
      closed: focus.closed,
      runs,
      focus,
      selected: this.selected,
      hovered: this.hovered,
      segment: this.segment,
    };
  }

  /** The same hit test as a press, so transform controls can yield to anchors and handles. */
  hit(world: Point): PathGrab | null {
    return this.at(world);
  }

  /** A context click targets an anchor without starting a drag or collapsing a chosen set. */
  contextSelect(world: Point): boolean {
    if (!this.active) return false;
    const found = this.at(world);
    if (found && !this.selected.includes(found.index)) this.selectIndices([found.index], false);
    // A handle belongs to its anchor; empty space keeps the current point selection.
    return true;
  }

  /** Follows the pointer so the overlay can show what is under it. */
  hover(world: Point): void {
    if (!this.id || this.grabbed) return;
    const found = this.at(world);
    const segment = found ? null : this.segmentNear(world);
    const changed =
      found?.kind !== this.hovered?.kind ||
      found?.index !== this.hovered?.index ||
      segment?.index !== this.segment?.index ||
      Math.abs((segment?.t ?? 0) - (this.segment?.t ?? 0)) > 0.002;
    this.hovered = found;
    // An anchor wins: the two are within a few pixels of each other at every corner, and
    // adding a point on top of one is never what the hand meant.
    this.segment = segment;
    if (changed) this.options.onChange();
  }

  /**
   * The piece of path under a point, if it is within reach of it.
   *
   * Takes an id so it can be asked about a shape that is not open — a cursor promising
   * to add a point has to be asking the same question the press will, or it promises
   * where the press will refuse.
   */
  segmentNear(world: Point, on: string | null = this.id): SegmentHover | null {
    const subpaths = on ? this.subpathsOf(on) : null;
    const runs = on ? this.runsOf(on) : null;
    if (!on || !subpaths || !runs) return null;
    const back = invert(this.options.scene.worldTransform(on));
    if (!back) return null;
    const local = applyToPoint(back, world.x, world.y);
    // The nearest piece of any subpath: a hole's edge is as much the shape as the outline.
    let nearest: (NonNullable<ReturnType<typeof nearestSegment>> & { run: number }) | null = null;
    for (let run = 0; run < subpaths.length; run++) {
      const { points, closed } = subpaths[run]!;
      if (points.length < 2) continue;
      const near = nearestSegment(points, closed, local);
      if (near && (!nearest || near.distance < nearest.distance)) nearest = { ...near, run };
    }
    if (!nearest) return null;
    const { run, index: piece } = nearest;
    // Measured on screen, so the reach is the same at every zoom.
    const out = this.options.scene.worldTransform(on);
    const here = this.options.toScreen(world);
    const there = this.options.toScreen(applyToPoint(out, nearest.at.x, nearest.at.y));
    if (Math.hypot(there.x - here.x, there.y - here.y) > SEGMENT_REACH) return null;
    const half = segmentsOf(subpaths[run]!.points, subpaths[run]!.closed)[piece];
    const place = (at: { x: number; y: number }) => applyToPoint(out, at.x, at.y);
    return {
      index: runs[run]!.start + piece,
      t: nearest.t,
      at: place(nearest.at),
      middle: half ? place(pointOnSegment(half, 0.5)) : place(nearest.at),
    };
  }

  /**
   * Puts a point at the middle of a segment, and chooses it.
   *
   * The curve keeps its shape — the split hands the anchors either side the controls that
   * make that true — so this reads as adding somewhere to hold rather than as an edit.
   */
  addPointAt(hover: SegmentHover, where: 'middle' | 'here' = 'middle'): number | null {
    const found = this.id ? this.locate(this.id, hover.index) : null;
    const subpaths = this.id ? this.subpathsOf(this.id) : null;
    if (!found || !subpaths) return null;
    const { points, closed } = subpaths[found.run]!;
    const next = withPointAdded(points, closed, found.local, where === 'here' ? hover.t : 0.5);
    if (next.length === points.length) return null;
    const added = hover.index + 1;
    this.selected = [added];
    this.segment = null;
    this.writeSubpaths(subpaths.map((subpath, run) => (run === found.run ? { closed, points: next } : subpath)));
    return added;
  }

  /** Takes one anchor out, wherever it is, leaving the rest of the path alone. */
  removeAt(index: number): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!this.id || !points || !points[index]) return;
    this.selected = [index];
    this.removeSelected();
  }

  /**
   * Turns anchors from corners into smooth points or back.
   *
   * Smoothing lays handles along the line between an anchor's neighbours, reaching a
   * third of the way towards each — the shape a curve through three points wants, and
   * the same rule the pen uses when it draws one. Sharpening takes the handles away,
   * which is what makes a corner: a corner is not a kind so much as the absence of
   * anything bending it.
   *
   * The whole selection goes at once when the anchor pressed is part of one. An edge is
   * a run of anchors, not an anchor — a painted outline has hundreds of them — so
   * smoothing one at a time is not a way anybody could smooth an edge. Which way they
   * all go is decided by the one under the hand, so a mixed run ends up agreeing rather
   * than each anchor flipping to its own opposite.
   */
  convertAt(index: number): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!this.id || !points) return;
    const point = points[index];
    if (!point) return;
    const chosen = this.selected.includes(index) ? this.selected : [index];
    this.selected = [...chosen];
    this.setSelectedKind(point.in || point.out ? 'corner' : 'smooth');
  }

  /** An explicit toolbar command, so applying Smooth twice never turns points into corners. */
  setSelectedKind(kind: 'corner' | 'smooth'): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    const subpaths = this.id ? this.subpathsOf(this.id) : null;
    const runs = this.id ? this.runsOf(this.id) : null;
    if (!this.id || !points || !subpaths || !runs || !this.selected.length || this.grabbed) return;
    const chosen = new Set(this.selected);
    const next = points.map((each, at) => {
      if (!chosen.has(at)) return { ...each };
      if (kind === 'corner') return { x: each.x, y: each.y, kind: 'corner' as const };
      if (each.kind === 'smooth' && (each.in || each.out)) return { ...each };
      // Smoothed against its neighbours on its own subpath, never the next one's first point.
      const run = runs.findIndex((r) => at >= r.start && at < r.start + r.count);
      const handles = run >= 0 ? smoothHandles(subpaths[run]!.points, subpaths[run]!.closed, at - runs[run]!.start) : null;
      return handles ? { x: each.x, y: each.y, kind: 'smooth' as const, ...handles } : { ...each };
    });
    this.write(next);
  }

  /** Chooses the two anchors a piece of path runs between, which is what moves it. */
  chooseSegment(index: number, extend: boolean): void {
    const next = this.nextOf(index);
    if (next === null) return;
    this.selectIndices([index, next], extend);
  }

  /**
   * The anchor after this one along its own subpath, or null at an open subpath's end —
   * which is also whether a segment starts here.
   */
  nextOf(index: number): number | null {
    const found = this.id ? this.locate(this.id, index) : null;
    if (!found) return null;
    const { at, local } = found;
    if (local + 1 < at.count) return index + 1;
    return at.closed && at.count > 1 ? at.start : null;
  }

  /** Where a segment runs on the page, for the overlay to trace it. */
  segmentPath(index: number): Point[] | null {
    const found = this.id ? this.locate(this.id, index) : null;
    const subpath = found ? this.subpathsOf(this.id!)?.[found.run] : null;
    if (!found || !subpath) return null;
    const segment = segmentsOf(subpath.points, subpath.closed)[found.local];
    if (!segment) return null;
    return Array.from({ length: 17 }, (_, step) => this.toWorld(pointOnSegment(segment, step / 16)));
  }

  /**
   * A press inside the path. Returns false when it lands on nothing, which the canvas
   * takes as "leave this path alone now".
   */
  press(world: Point, extend: boolean): boolean {
    if (!this.id) return false;
    const found = this.at(world);
    if (!found) return false;

    this.grabbed = found;
    this.from = world;
    this.before = { ...this.pointsOf(this.id)![found.index]! };
    this.beforeAll = this.snapshot();
    if (found.kind === 'anchor') {
      if (extend) {
        this.selected = this.selected.includes(found.index)
          ? this.selected.filter((index) => index !== found.index)
          : [...this.selected, found.index];
      } else if (!this.selected.includes(found.index)) {
        this.selected = [found.index];
      }
      // The mirror takes in whatever the press just chose, so a symmetrical edit needs
      // only one hand.
      if (this.symmetric) this.selectIndices(this.selected, false);
    } else {
      this.selected = [found.index];
    }
    this.options.onChange();
    return true;
  }

  /**
   * Moves whatever was grabbed.
   *
   * `alt` breaks a handle pair so the sides move apart. `free` is command or control:
   * leave it exactly where I put it, no lining up. Alt cannot be the key for that — it
   * pans the canvas whatever tool is in hand, so a drag holding it never reaches here.
   */
  drag(world: Point, alt: boolean, free = false): void {
    const grabbed = this.grabbed;
    const before = this.before;
    if (!this.id || !grabbed || !before || !this.from) return;
    const points = this.pointsOf(this.id);
    if (!points) return;
    // The points changed count under the hand (an undo, a collaborator deleting one): the
    // index held no longer names the point that was taken, and writing on would overwrite a
    // neighbour — or throw, on a handle. The drag ends there (QA 2026-09-25, select-points-F20).
    if (!this.beforeAll || points.length !== this.beforeAll.length || !points[grabbed.index]) {
      this.release();
      return;
    }

    const start = this.toLocal(this.from);
    const now = this.toLocal(world);
    const dx = now.x - start.x;
    const dy = now.y - start.y;
    const next = [...points];

    if (grabbed.kind === 'anchor') {
      // Taking hold of one of several chosen anchors carries all of them. Pressing an
      // anchor that is already chosen leaves the choosing alone, so the hand has said
      // "move this lot" rather than "move this one", and only moving the one under the
      // finger would be answering a question nobody asked.
      if (this.beforeAll && this.selected.includes(grabbed.index)) {
        let mx = world.x - this.from.x;
        let my = world.y - this.from.y;
        // Where the anchor under the hand would land, and what it would be level with
        // there. Command says "leave it exactly where I put it", as it does for shapes.
        const held = this.beforeAll[grabbed.index]!;
        const going = applyToPoint(this.options.scene.worldTransform(this.id), held.x, held.y);
        const snap = free
          ? { dx: 0, dy: 0, lines: [] }
          : snapDragPoint(
              { x: going.x + mx, y: going.y + my },
              { targets: this.settled(), guides: this.options.snapGuides() },
              this.options.snapSettings(),
              this.options.snapReach(),
            );
        mx += snap.dx;
        my += snap.dy;
        this.agreed = snap.lines;
        this.transformChosen(this.beforeAll, [1, 0, 0, 1, mx, my]);
        return;
      }
      // The handles belong to the anchor, so they travel with it.
      next[grabbed.index] = {
        ...before,
        x: before.x + dx,
        y: before.y + dy,
        ...(before.in ? { in: [before.in[0] + dx, before.in[1] + dy] as [number, number] } : {}),
        ...(before.out ? { out: [before.out[0] + dx, before.out[1] + dy] as [number, number] } : {}),
      };
    } else {
      const anchor = next[grabbed.index]!;
      // A handle lines up like anything else, and against its OWN anchor before anything
      // on screen: a handle level with the point it belongs to is a horizontal or vertical
      // tangent, which is the one alignment a curve is drawn by far the most often.
      const out = this.options.scene.worldTransform(this.id);
      const owner = applyToPoint(out, anchor.x, anchor.y);
      const snap = free
        ? { dx: 0, dy: 0, lines: [] }
        : snapDragPoint(
            world,
            { targets: [owner, ...this.settled()], guides: this.options.snapGuides() },
            this.options.snapSettings(),
            this.options.snapReach(),
          );
      this.agreed = snap.lines;
      const landed = this.toLocal({ x: world.x + snap.dx, y: world.y + snap.dy });
      const moved: [number, number] = [landed.x, landed.y];
      const opposite = grabbed.kind === 'out' ? 'in' : 'out';
      const keepTogether = !alt && (anchor.kind === 'mirrored' || anchor.kind === 'smooth');
      const mirror: [number, number] = [2 * anchor.x - moved[0], 2 * anchor.y - moved[1]];
      next[grabbed.index] = {
        ...anchor,
        kind: alt ? 'corner' : anchor.kind,
        [grabbed.kind]: moved,
        ...(keepTogether ? { [opposite]: anchor.kind === 'mirrored' ? mirror : keepDirection(anchor, opposite, moved) } : {}),
      } as PathPoint;
    }

    this.write(next);
  }

  release(): void {
    this.agreed = [];
    this.grabbed = null;
    this.from = null;
    this.before = null;
    this.beforeAll = null;
  }

  /** Delete removes the selected anchors; Escape and Enter leave the path alone. */
  handleKey(event: KeyboardEvent): boolean {
    if (!this.id) return false;
    // The path went (undo, a collaborator): nothing here to act on any more.
    if (!this.options.scene.get(this.id)) {
      this.close();
      return false;
    }
    if (event.key === 'Escape' || event.key === 'Enter') {
      event.preventDefault();
      this.close();
      return true;
    }
    if ((event.key === 'Backspace' || event.key === 'Delete') && this.selected.length > 0) {
      event.preventDefault();
      // Not while a point is still held: the drag would go on writing at an index that
      // now names a different point (select-points-F20).
      if (!this.grabbed) this.removeSelected();
      return true;
    }
    // Nor undo, for the same reason — kept here so the file's undo cannot reach under the
    // hand either.
    if (this.grabbed && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      return true;
    }
    if (event.key === 'a' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.selected = (this.pointsOf(this.id) ?? []).map((_, index) => index);
      this.options.onChange();
      return true;
    }
    // With points chosen, the arrows move THEM (Shift for ten), and Cmd+X does not cut
    // the whole shape from under them. Both fell through to the layer commands, which
    // nudged or cut the entire path (QA 2026-09-24, select-points-F5).
    const arrow = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key] as [number, number] | undefined;
    if (arrow && this.selected.length > 0 && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      const step = event.shiftKey ? 10 : 1;
      this.nudgeSelected(arrow[0] * step, arrow[1] * step);
      return true;
    }
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'x' && this.selected.length > 0) {
      event.preventDefault();
      return true;
    }
    return false;
  }

  /** Moves the chosen anchors, and their handles with them, by a distance on the page. */
  private nudgeSelected(dx: number, dy: number): void {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!this.id || !points) return;
    const inverse = invert(this.options.scene.worldTransform(this.id));
    // A step on the page, in the path's own axes.
    const lx = inverse ? inverse[0] * dx + inverse[2] * dy : dx;
    const ly = inverse ? inverse[1] * dx + inverse[3] * dy : dy;
    const moved = points.map((point, index) =>
      this.selected.includes(index)
        ? {
            ...point,
            x: point.x + lx,
            y: point.y + ly,
            ...(point.in ? { in: [point.in[0] + lx, point.in[1] + ly] as [number, number] } : {}),
            ...(point.out ? { out: [point.out[0] + lx, point.out[1] + ly] as [number, number] } : {}),
          }
        : point,
    );
    this.write(moved);
  }

  /** Takes out the chosen anchors, and the path itself when too few are left. */
  removeSelected(): void {
    const subpaths = this.id ? this.subpathsOf(this.id) : null;
    const runs = this.id ? this.runsOf(this.id) : null;
    if (!this.id || !subpaths || !runs || this.selected.length === 0) return;
    const chosen = new Set(this.selected);
    // A subpath needs two points to be anything; one left with fewer goes, so deleting
    // every point of a hole removes the hole and leaves the outline alone.
    const kept = subpaths
      .map((subpath, run) => ({ closed: subpath.closed, points: subpath.points.filter((_, local) => !chosen.has(runs[run]!.start + local)) }))
      .filter((subpath) => subpath.points.length >= 2);
    if (kept.length === 0) {
      const id = this.id;
      this.close();
      applyOps(this.options.page, [{ op: 'delete', id }], { scene: this.options.scene, origin: this.options.origin });
      return;
    }
    this.selected = [];
    this.writeSubpaths(kept);
  }

  /**
   * Simplify points: every subpath refitted with as few anchors as keep its shape, points
   * on top of each other merged. False when there was nothing to take out.
   */
  simplify(): boolean {
    const id = this.id;
    const subpaths = id ? this.subpathsOf(id) : null;
    if (!id || !subpaths || this.grabbed) return false;
    const before: VectorPath = { fillRule: 'nonzero', subpaths: [...subpaths] };
    const after = simplifyVectorPath(before);
    if (anchorCount(after) >= anchorCount(before)) return false;
    // The indices chosen named anchors that are no longer there.
    this.selected = [];
    this.grown = [];
    this.hovered = null;
    this.segment = null;
    this.writeSubpaths(after.subpaths);
    return true;
  }

  /** Joins the path's ends, or opens them again. */
  toggleClosed(): void {
    const subpaths = this.id ? this.subpathsOf(this.id) : null;
    const runs = this.id ? this.runsOf(this.id) : null;
    if (!this.id || !subpaths || !runs) return;
    // The subpath the chosen points are on: closing an "e" never touches its counter.
    const focus = runs.indexOf(this.focusRun(runs));
    const subpath = subpaths[focus];
    if (!subpath || subpath.points.length < 3) return;
    this.writeSubpaths(subpaths.map((each, run) => (run === focus ? { closed: !each.closed, points: each.points } : each)));
  }

  /** Whether the focused subpath's ends are joined, for a button that has to say which it does. */
  get closed(): boolean {
    const runs = this.id ? this.runsOf(this.id) : null;
    return runs ? this.focusRun(runs).closed : false;
  }

  /**
   * The one anchor chosen, where it sits on the page — and a way to put it somewhere else.
   *
   * In page coordinates rather than the path's own, because that is what a person reads
   * off a ruler and what every other pair of numbers in the editor means. The round trip
   * back into the path's coordinates is this class's business, not theirs.
   */
  get lone(): { index: number; at: Point } | null {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points || this.selected.length !== 1) return null;
    const index = this.selected[0]!;
    const point = points[index];
    if (!point) return null;
    return { index, at: applyToPoint(this.options.scene.worldTransform(this.id!), point.x, point.y) };
  }

  /** Puts the one chosen anchor at a place on the page, handles travelling with it. */
  placeLone(at: Point): void {
    const lone = this.lone;
    const before = this.snapshot();
    if (!lone || !before) return;
    this.transformChosen(before, [1, 0, 0, 1, at.x - lone.at.x, at.y - lone.at.y]);
  }

  /** How many anchors are chosen, which is what the rail's buttons turn on. */
  get chosenCount(): number {
    return this.selected.length;
  }

  // ── the document ─────────────────────────────────────────────────────

  /**
   * Writes every anchor back, in the one run of indices, to the subpaths they came from.
   * The count has to be the one read: adding and removing go through `writeSubpaths`.
   */
  private write(points: PathPoint[]): void {
    const id = this.id;
    const runs = id ? this.runsOf(id) : null;
    if (!id || !runs) return;
    const total = runs.reduce((sum, run) => sum + run.count, 0);
    if (points.length !== total) return;
    this.writeSubpaths(runs.map((run) => ({ closed: run.closed, points: points.slice(run.start, run.start + run.count) })));
  }

  /**
   * The whole path, subpaths and fill rule together. Every hole and every other part of a
   * compound shape — any Shape Builder or Merge result — is written back as it is, never
   * dropped (QA 2026-09-24, select-points-F1, pen-F6).
   */
  private writeSubpaths(subpaths: Subpath[]): void {
    const id = this.id;
    if (!id) return;
    const stored = this.options.scene.get(id);
    const current = stored?.type === 'path' ? stored.path : undefined;
    const path: VectorPath = { fillRule: current?.fillRule ?? 'nonzero', subpaths };
    applyOps(this.options.page, [{ op: 'set', id, props: { path } }], {
      scene: this.options.scene,
      origin: this.options.origin,
    });
    this.options.onChange();
  }

  private subpathsOf(id: string): readonly Subpath[] | null {
    const node = this.options.scene.get(id);
    if (!node || node.type !== 'path') return null;
    const subpaths = node.path?.subpaths;
    return subpaths && subpaths.length > 0 ? subpaths : null;
  }

  /** Where each subpath's anchors sit in the one run of indices. */
  private runsOf(id: string): SubpathRun[] | null {
    const subpaths = this.subpathsOf(id);
    if (!subpaths) return null;
    let start = 0;
    return subpaths.map(({ points, closed }) => {
      const run = { start, count: points.length, closed };
      start += points.length;
      return run;
    });
  }

  /** Which subpath an index is on, and where on it. */
  private locate(id: string, index: number): { run: number; at: SubpathRun; local: number } | null {
    const runs = this.runsOf(id);
    if (!runs || index < 0) return null;
    const run = runs.findIndex((each) => index >= each.start && index < each.start + each.count);
    if (run < 0) return null;
    return { run, at: runs[run]!, local: index - runs[run]!.start };
  }

  /** The subpath the first chosen anchor is on, or the first one: what Close path means. */
  private focusRun(runs: readonly SubpathRun[]): SubpathRun {
    const first = this.selected[0];
    return (first !== undefined && runs.find((run) => first >= run.start && first < run.start + run.count)) || runs[0]!;
  }

  /** A selection rule that knows only one subpath, applied to each subpath in turn. */
  private perRun(chosen: readonly number[], rule: (chosen: number[], count: number, closed: boolean) => number[]): number[] {
    const runs = this.id ? this.runsOf(this.id) : null;
    if (!runs) return [...chosen];
    return runs.flatMap((run) => {
      const mine = chosen.filter((index) => index >= run.start && index < run.start + run.count).map((index) => index - run.start);
      return mine.length ? rule(mine, run.count, run.closed).map((local) => local + run.start) : [];
    });
  }

  /**
   * Every subpath's anchors as one list, which is what an index counts along. Kept per
   * stored subpaths so a press or a hover does not copy a traced outline's thousands of
   * points each time it asks; with one subpath it is that subpath's own list.
   */
  private pointsOf(id: string): PathPoint[] | null {
    const subpaths = this.subpathsOf(id);
    if (!subpaths) return null;
    if (subpaths.length === 1) return subpaths[0]!.points;
    const kept = flattened.get(subpaths);
    if (kept) return kept;
    const all = subpaths.flatMap((subpath) => subpath.points);
    flattened.set(subpaths, all);
    return all;
  }

  /** The anchor or handle under the pointer. Handles win: they sit on top. */
  private at(world: Point): PathGrab | null {
    const points = this.id ? this.pointsOf(this.id) : null;
    if (!points) return null;
    const pointer = this.options.toScreen(world);
    const away = (local: Point) => {
      const at = this.options.toScreen(this.toWorld(local));
      return Math.hypot(at.x - pointer.x, at.y - pointer.y);
    };
    // The NEAREST thing within reach, points winning ties and anything within a couple of
    // pixels of them. Handles of chosen points used to be asked first and the first one
    // within reach taken, so on a dense path a neighbour's handle beat the point under
    // the pointer (QA 2026-09-24, select-points-N1).
    let best: { grab: PathGrab; distance: number } | null = null;
    const offer = (grab: PathGrab, distance: number) => {
      if (distance <= REACH && (!best || distance < best.distance)) best = { grab, distance };
    };
    // Among points right under the pointer, the NEAREST — the first in path order used to
    // win, so of two points a couple of pixels apart the press could take the neighbour
    // while hover lit the right one (QA 2026-09-25, select-points-F12).
    let under: { index: number; distance: number } | null = null;
    for (let index = 0; index < points.length; index++) {
      const distance = away(points[index]!);
      if (distance <= POINT_FIRST && (!under || distance < under.distance)) under = { index, distance };
      offer({ kind: 'anchor', index }, distance - 0.5);
    }
    if (under) return { kind: 'anchor', index: under.index };
    for (const index of this.selected) {
      const point = points[index];
      if (!point) continue;
      // A handle lying on its own point is not a separate thing to grab.
      const own = away(point);
      if (point.out) {
        const distance = away({ x: point.out[0], y: point.out[1] });
        if (Math.abs(distance - own) > 1 || own > REACH) offer({ kind: 'out', index }, distance);
      }
      if (point.in) {
        const distance = away({ x: point.in[0], y: point.in[1] });
        if (Math.abs(distance - own) > 1 || own > REACH) offer({ kind: 'in', index }, distance);
      }
    }
    return (best as { grab: PathGrab } | null)?.grab ?? null;
  }

  private toLocal(world: Point): Point {
    const inverse = this.id ? invert(this.options.scene.worldTransform(this.id)) : null;
    return inverse ? applyToPoint(inverse, world.x, world.y) : world;
  }

  private toWorld(local: Point): Point {
    if (!this.id) return local;
    return applyToPoint(this.options.scene.worldTransform(this.id), local.x, local.y);
  }
}

/** Every subpath's anchors as one list, per stored list of subpaths. */
const flattened = new WeakMap<readonly Subpath[], PathPoint[]>();

/** Keeps a smooth point's other handle in line, at the length it already had. */
function keepDirection(anchor: PathPoint, side: 'in' | 'out', moved: [number, number]): [number, number] {
  const other = anchor[side];
  const dx = anchor.x - moved[0];
  const dy = anchor.y - moved[1];
  const length = Math.hypot(dx, dy);
  if (!other || length < 1e-6) return [anchor.x + dx, anchor.y + dy];
  const keep = Math.hypot(other[0] - anchor.x, other[1] - anchor.y);
  return [anchor.x + (dx / length) * keep, anchor.y + (dy / length) * keep];
}

/** Two selections holding the same indices, whatever order they arrived in. */
function same(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const all = new Set(a);
  return b.every((index) => all.has(index));
}
